import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import API from '../utils/api';
// NOTE: adjust the '../utils/api' import path above to match wherever this
// file actually lives relative to your utils/ folder (same rule as the
// existing TokenPage.js / TokenGeneration.js imports).

// Column order = template order. "Phone Number" is the only optional column.
const TEMPLATE_HEADERS = [
  'Client Name', 'Facility Code', 'Access Code', 'Employee ID',
  'First Name', 'Last Name', 'Employee Email', 'Phone Number',
  'Valid From', 'Valid To',
];
const REQUIRED_HEADERS = TEMPLATE_HEADERS.filter((h) => h !== 'Phone Number');
const HEADER_KEY_MAP = {
  'Client Name': 'clientName',
  'Facility Code': 'facilityCode',
  'Access Code': 'accessCode',
  'Employee ID': 'employeeId',
  'First Name': 'firstName',
  'Last Name': 'lastName',
  'Employee Email': 'employeeEmail',
  'Phone Number': 'phone',
  'Valid From': 'validFrom',
  'Valid To': 'validUntil',
};
const DATE_KEYS = ['validFrom', 'validUntil'];
const MAX_ROWS = 2000; // keep in sync with MAX_BULK_ROWS on the backend

// ── Date cell → ISO string. Handles real Excel date cells (read as Date
//    objects thanks to cellDates: true) and typed text like "2026-10-01 09:00".
//    Unparseable text is passed through as-is so the server reports it. ──────
const cellToDateTime = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  if (v instanceof Date) {
    // Round to the nearest minute — SheetJS can be a few seconds off
    const ms = Math.round(v.getTime() / 60000) * 60000;
    return new Date(ms).toISOString();
  }
  const d = new Date(String(v).trim());
  return isNaN(d.getTime()) ? String(v).trim() : d.toISOString();
};

const UploadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const WarnIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ── Popup shown BEFORE anything is sent, when rows have duplicate / invalid
//     details. Nothing is generated until the file is fixed and re-uploaded. ──
const ConflictModal = ({ conflicts, total, onClose, onDownload }) => (
  <div className="modal-backdrop" onClick={onClose}>
    <div className="modal-box" style={{ maxWidth: 520, textAlign: 'left' }} onClick={(e) => e.stopPropagation()}>
      <div className="modal-icon-wrap modal-icon-wrap--orange" style={{ margin: '0 0 12px' }}>
        <WarnIcon />
      </div>
      <h3 className="modal-title" style={{ margin: 0 }}>Details Already in Use</h3>
      <p className="modal-desc" style={{ marginTop: 6 }}>
        {conflicts.length} of {total} row{total !== 1 ? 's' : ''} have duplicate or invalid details.
        No tokens were generated. Fix these rows in your Excel file and upload it again.
      </p>
      <div style={{ maxHeight: '45vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', marginTop: 10 }}>
        {conflicts.map((c, idx) => (
          <div key={idx} style={{
            padding: '9px 12px', fontSize: '0.8rem',
            borderBottom: idx < conflicts.length - 1 ? '1px solid #f3f4f6' : 'none',
          }}>
            <div style={{ fontWeight: 600, color: '#111827', overflowWrap: 'anywhere' }}>
              Row {c.row} — {c.employeeEmail || '(no email)'}
            </div>
            <div style={{ color: '#b91c1c', marginTop: 2 }}>{c.message}</div>
          </div>
        ))}
      </div>
      <div className="modal-actions" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button className="modal-btn modal-btn--cancel" onClick={onDownload}>Download List</button>
        <button className="modal-btn modal-btn--orange" onClick={onClose}>OK</button>
      </div>
    </div>
  </div>
);

// ── Row-by-row details, shown as a popup so it never competes with the
//     single progress bar on the main panel — works live while the batch
//     is still running, since jobStatus updates on every poll tick. ──────────
const RowDetailsModal = ({ jobStatus, onClose }) => (
  <div className="modal-backdrop" onClick={onClose}>
    <div className="modal-box" style={{ maxWidth: 480, textAlign: 'left' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 className="modal-title" style={{ margin: 0 }}>Import Details</h3>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
          <CloseIcon />
        </button>
      </div>
      <p className="modal-desc" style={{ marginTop: 4 }}>
        {jobStatus.processed} of {jobStatus.total} processed
        {jobStatus.completed ? ` — ${jobStatus.results.filter((r) => r.status === 'success').length} sent, ${jobStatus.results.filter((r) => r.status === 'failed').length} failed.` : '…'}
      </p>
      <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', marginTop: 10 }}>
        {jobStatus.results.length === 0 ? (
          <p style={{ padding: 14, fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>No rows processed yet.</p>
        ) : (
          jobStatus.results.map((r, idx) => (
            <div key={idx} style={{
              display: 'flex', justifyContent: 'space-between', gap: 10,
              padding: '9px 12px', fontSize: '0.8rem',
              borderBottom: idx < jobStatus.results.length - 1 ? '1px solid #f3f4f6' : 'none',
              color: r.status === 'success' ? '#166534' : '#b91c1c',
            }}>
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>Row {r.row} — {r.employeeEmail || '(no email)'}</span>
              <span style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>{r.status === 'success' ? '✓ sent' : r.message}</span>
            </div>
          ))
        )}
      </div>
      <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
        <button className="modal-btn modal-btn--cancel" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>
);

/**
 * Reusable Excel bulk-import panel for token generation.
 *
 * Props:
 *  - importEndpoint: string — base API path (e.g. '/admin/token' or the
 *    `apiBase` prop already used for the single-token flow). Panel POSTs to
 *    `${importEndpoint}/bulk-import/check` (dry run), then
 *    `${importEndpoint}/bulk-import`, and polls `${importEndpoint}/bulk-import/:id/status`.
 *  - onComplete: () => void — called once the job finishes, so the parent
 *    page can refresh its token history (and quota, for vendor/user).
 */
const ExcelImportPanel = ({ importEndpoint, onComplete }) => {
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // { total, processed, completed, results }
  const [showDetails, setShowDetails] = useState(false);
  const [conflicts, setConflicts] = useState(null); // null | [{ row, employeeEmail, message }]
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setFileName(''); setParsedRows([]); setParseError('');
    setSubmitting(false); setJobStatus(null); setShowDetails(false); setConflicts(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const togglePanel = () => {
    if (open) reset();
    setOpen((o) => !o);
  };

  // ── Download a blank template with one example row ─────────────────────────
  // Dates are written as TEXT so they show exactly as typed; real Excel date
  // cells also work when uploading.
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_HEADERS,
      ['Acme Corp', 926, 1234, 'EMP001', 'Jane', 'Doe', 'jane.doe@example.com', '+919876543210', '2026-10-01 09:00', '2026-10-02 09:00'],
    ]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tokens');
    XLSX.writeFile(wb, 'token_import_template.xlsx');
  };

  // ── Parse the uploaded file client-side (format/preview check only —
  //     the backend re-validates every row before generating anything) ───────
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setParseError(''); setParsedRows([]); setJobStatus(null); setConflicts(null); setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array', cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (raw.length === 0) { setParseError('That file has no data rows.'); return; }

        const actualHeaders = Object.keys(raw[0]).map((k) => k.trim().toLowerCase());
        const missing = REQUIRED_HEADERS.filter((h) => !actualHeaders.includes(h.toLowerCase()));
        if (missing.length > 0) {
          setParseError(`Missing column(s): ${missing.join(', ')}. Use "Download Template" for the exact format.`);
          return;
        }

        const mapped = raw.map((r) => {
          const row = {};
          Object.entries(HEADER_KEY_MAP).forEach(([header, key]) => {
            const actualKey = Object.keys(r).find((k) => k.trim().toLowerCase() === header.toLowerCase());
            const value = actualKey !== undefined ? r[actualKey] : '';
            row[key] = DATE_KEYS.includes(key) ? cellToDateTime(value) : String(value).trim();
          });
          return row;
        });

        if (mapped.length > MAX_ROWS) {
          setParseError(`${mapped.length} rows found — max ${MAX_ROWS} per import. Split the file and upload in batches.`);
          return;
        }

        setParsedRows(mapped);
      } catch (err) {
        setParseError('Could not read this file. Make sure it is a valid .xlsx or .xls file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Poll the backend job for progress ───────────────────────────────────────
  const poll = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await API.get(`${importEndpoint}/bulk-import/${jobId}/status`);
        setJobStatus(res.data);
        if (res.data.completed) {
          clearInterval(pollRef.current);
          setSubmitting(false);
          onComplete && onComplete();
        }
      } catch {
        clearInterval(pollRef.current);
        setSubmitting(false);
        setParseError('Lost connection while checking import progress. Check Token History to see what went through.');
      }
    }, 1500);
  };

  // ── Step 1: dry-run check against token history + the file itself.
  //    Step 2: only if everything is clean, start the real import. ────────────
  const startImport = async () => {
    setSubmitting(true); setParseError(''); setJobStatus(null); setConflicts(null);
    try {
      try {
        const chk = await API.post(`${importEndpoint}/bulk-import/check`, { rows: parsedRows });
        if (chk.data.conflicts && chk.data.conflicts.length > 0) {
          setConflicts(chk.data.conflicts);
          setSubmitting(false);
          return; // nothing is generated until the file is fixed
        }
      } catch (err) {
        // Endpoints without the /check route (e.g. other roles) skip the dry run
        // — the import job itself still rejects duplicates row by row.
        if (err.response?.status !== 404) throw err;
      }

      const res = await API.post(`${importEndpoint}/bulk-import`, { rows: parsedRows });
      poll(res.data.jobId);
    } catch (err) {
      setSubmitting(false);
      setParseError(err.response?.data?.message || 'Failed to start the import.');
    }
  };

  // Closing the popup clears the loaded file so the fixed version can be picked
  // (browsers don't fire onChange when the same file is chosen again).
  const closeConflicts = () => {
    setConflicts(null);
    setFileName(''); setParsedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadConflicts = () => {
    if (!conflicts || conflicts.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(conflicts.map((c) => ({ Row: c.row, 'Employee Email': c.employeeEmail, Problem: c.message })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rows To Fix');
    XLSX.writeFile(wb, 'rows_to_fix.xlsx');
  };

  const downloadFailedReport = () => {
    const failed = (jobStatus?.results || []).filter((r) => r.status === 'failed');
    if (failed.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(failed.map((f) => ({ Row: f.row, 'Employee Email': f.employeeEmail, Reason: f.message })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Rows');
    XLSX.writeFile(wb, 'failed_import_rows.xlsx');
  };

  const successCount = (jobStatus?.results || []).filter((r) => r.status === 'success').length;
  const failedCount = (jobStatus?.results || []).filter((r) => r.status === 'failed').length;
  const pct = jobStatus && jobStatus.total > 0 ? Math.round((jobStatus.processed / jobStatus.total) * 100) : 0;

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <button type="button" className="tg-btn" style={{ background: '#fff', color: '#1565c0', border: '1.5px solid #90caf9' }} onClick={togglePanel}>
        <UploadIcon /> {open ? 'Close Excel Import' : 'Import from Excel'}
      </button>

      {open && (
        <div style={{ marginTop: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>

          {!jobStatus && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151' }}>
                  Upload an Excel file to generate and email tokens for many users at once.
                </p>
                <button type="button" onClick={downloadTemplate}
                  style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1565c0', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Download Template
                </button>
              </div>
              <p style={{ margin: '0 0 14px', fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.5 }}>
                Email, Employee ID and the Facility Code + Access Code pair must all be new — anything already
                in Token History (or repeated in the file) is rejected. Dates: <strong>YYYY-MM-DD HH:mm</strong> or Excel date cells.
              </p>

              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFile} disabled={submitting}
                style={{ fontSize: '0.85rem' }} />

              {fileName && parsedRows.length > 0 && !parseError && (
                <p style={{ marginTop: 10, fontSize: '0.8rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileIcon /> {fileName} — {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} ready to import
                </p>
              )}

              {parseError && (
                <div className="tg-banner tg-banner--error" style={{ marginTop: 10 }}>
                  <span className="tg-banner-icon">✕</span>
                  <div><p className="tg-banner-sub" style={{ margin: 0 }}>{parseError}</p></div>
                </div>
              )}

              {parsedRows.length > 0 && !parseError && (
                <button type="button" className="tg-btn" style={{ marginTop: 14 }} disabled={submitting} onClick={startImport}>
                  {submitting ? <><span className="tg-spinner" /> Checking details...</> : `Generate ${parsedRows.length} Token${parsedRows.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </>
          )}

          {jobStatus && (
            <div style={{ textAlign: 'center', padding: '8px 4px' }}>

              {/* ── Status icon ── */}
              <div style={{
                width: 52, height: 52, borderRadius: '50%', margin: '0 auto 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: jobStatus.completed ? '#dcfce7' : '#e3f2fd',
                transition: 'background 0.3s ease',
              }}>
                {jobStatus.completed ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span className="tg-spinner" style={{ width: 22, height: 22, borderWidth: 2.5, borderTopColor: '#1976d2' }} />
                )}
              </div>

              {/* ── Single, unified status message ── */}
              <p style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>
                {jobStatus.completed
                  ? 'All tokens generated and emails sent'
                  : 'Generating tokens and sending emails…'}
              </p>
              <p style={{ margin: '0 0 18px', fontSize: '0.8rem', color: '#6b7280' }}>
                {jobStatus.completed
                  ? `${successCount} of ${jobStatus.total} sent successfully${failedCount > 0 ? `, ${failedCount} failed` : ''}.`
                  : 'Processing employees — this may take a moment.'}
              </p>

              {/* ── ONE overall progress bar for the whole batch ── */}
              <div style={{ height: 10, borderRadius: 6, background: '#e5e7eb', overflow: 'hidden', marginBottom: 8 }}>
                <div style={{
                  height: '100%', width: `${pct}%`,
                  background: jobStatus.completed ? '#16a34a' : 'linear-gradient(90deg, #42a5f5, #1976d2)',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <p style={{ margin: '0 0 18px', fontSize: '0.78rem', color: '#9ca3af' }}>
                {jobStatus.processed} of {jobStatus.total} employees processed ({pct}%)
              </p>

              {jobStatus.completed && failedCount > 0 && (
                <div className="tg-banner tg-banner--error" style={{ textAlign: 'left', marginBottom: 14 }}>
                  <span className="tg-banner-icon">✕</span>
                  <div>
                    <p className="tg-banner-sub" style={{ margin: 0 }}>
                      {failedCount} row{failedCount !== 1 ? 's' : ''} could not be sent. Download the report below for details.
                    </p>
                  </div>
                </div>
              )}

              {/* ── Row-by-row detail lives in a popup, not inline — keeps this panel
                     to exactly one progress indicator no matter how many rows there are.
                     Available while running too, since jobStatus.results grows live. ── */}
              {jobStatus.results.length > 0 && (
                <button type="button" onClick={() => setShowDetails(true)}
                  style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1565c0', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 14, textDecoration: 'underline' }}>
                  View row-by-row details ({jobStatus.results.length}/{jobStatus.total})
                </button>
              )}

              {jobStatus.completed && (
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  {failedCount > 0 && (
                    <button type="button" className="tg-btn" style={{ background: '#fff', color: '#b91c1c', border: '1.5px solid #fecaca' }} onClick={downloadFailedReport}>
                      Download Failed Rows
                    </button>
                  )}
                  <button type="button" className="tg-btn" style={{ background: '#fff', color: '#374151', border: '1.5px solid #d1d5db' }} onClick={reset}>
                    Import Another File
                  </button>
                </div>
              )}
            </div>
          )}

          {showDetails && jobStatus && (
            <RowDetailsModal jobStatus={jobStatus} onClose={() => setShowDetails(false)} />
          )}

          {conflicts && conflicts.length > 0 && (
            <ConflictModal
              conflicts={conflicts}
              total={parsedRows.length}
              onClose={closeConflicts}
              onDownload={downloadConflicts}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ExcelImportPanel;